import { CodeMapSet } from "@app/codemaps";
import { ClientInsuranceModel, InsuranceList } from "@app/insurance";
import { AccessCriteriaGranularityEnum, TemplateCollectionMetadata, TemplateSpecialtyGroup } from "@app/templates";
import first from "lodash/first";
import * as React from "react";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { EditPerspective, TimeDistanceCalculatorType } from "../enumerators";
import EditAdequacyTemplateOptions from "./EditAdequacyTemplateOptions";
import Project from "@app/projects/project";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContextProps } from "@app/user/UserContext";
import { AccuracySpecialtyDefinition, AccuracyTemplateCollectionMetadata } from "@app/templates/accuracy/models";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface TemplateOptionProps {
  accuracyInsurancesIsLoading: boolean;
  accuracyTemplateCollections: AccuracyTemplateCollectionMetadata[];
  accuracySpecialtyDefinitions: AccuracySpecialtyDefinition[];
  adequacyTemplateCollectionIdRef: React.RefObject<HTMLSelectElement>;
  authorizedInsurances: ClientInsuranceModel[];
  checkboxClass: string;
  disabled: boolean;
  handleAddInspectedInsurance: () => void;
  handleRemoveInspectedInsurance: (uid: string) => void;
  handleUseAccuracyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUseAdequacyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAuthorizedInsurancesChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isZipProject: boolean;
  onAccessCodeMapSetChange: (accessCodeMapSetId: number) => void;
  onAccuracySpecialtyDefinitionIdChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onAccuracySpecialtyToQdpCodeMapChange: (value: number) => void;
  onAccuracyTemplateCollectionChange: (templateCollectionId: string) => void;
  onAccuracyTemplateCorrelationIdChange: (templateCorrelationId: string) => void;
  onCheckBoxChange: (property: UpdateResourceKeys) => void;
  onExpandSpecialtyMileageChange: (value: number) => void;
  onMarketChanged: (value: string) => void;
  onMarketSpecialtyCodeMapChange: (value: number) => void;
  onNetworkSpecialtyCodeMapChange: (value: number) => void;
  onSpecialtyGroupsChange: (selectedRecords: TemplateSpecialtyGroup[]) => void;
  onTelehealthSpecialtyGroupsChange: (selectedSpecialtyGroupCodes: string[]) => void;
  onTemplateCollectionChange: (templateCollectionId: number) => void;
  onTemplateCorrelationIdChange: (templateCorrelationId: string, userRoles: string[]) => void;
  onTimeDistanceCalculatorTypeChange: (timeDistanceCalculatorType: TimeDistanceCalculatorType) => void;
  perspective: EditPerspective;
  project: Project;
  projectAccessCodeMapSets: CodeMapSet[];
  expandSpecialtyMileageCodeMapSets: CodeMapSet[];
  selectedInsurances: ClientInsuranceModel[];
  selectedInsuranceUid: string;
  shared: boolean;
  showNetworkSpecialtyCodeMap: boolean;
  sortedSpecialtyGroups: TemplateSpecialtyGroup[];
  specialtyCodeMapSets: CodeMapSet[];
  specialtyToQdpCodeMapSets: CodeMapSet[];
  templateCollections: TemplateCollectionMetadata[];
  templateAccessGranularity: AccessCriteriaGranularityEnum;
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[];
  useAccuracy: boolean;
  useAdequacy: boolean;
}

interface TemplateOptionsState {
  previousTimeDistanceCalculatorType: TimeDistanceCalculatorType;
}

type UpdateResourceKeys = keyof Project;

export class TemplateOptions extends React.Component<TemplateOptionProps & UserContextProps, TemplateOptionsState> {

  constructor(props: TemplateOptionProps & UserContextProps) {
    super(props);

    this.state = {
      previousTimeDistanceCalculatorType: null,
    };
  }

  public render() {
    const hasAccuracyTemplates = this.props.accuracyTemplateCollections && this.props.accuracyTemplateCollections.length > 0;

    return (
      <React.Fragment>
        <div className="tab-contents col-12">
          <RoleRestricted allowedRoles={[Roles.ACCURACY_LICENSE_TIER_1]}>
            {(hasAccuracyTemplates || this.props.shared) && (
              <>
                {!this.props.shared && !this.props.isZipProject && (
                  <>
                    <div className="form-group row">
                      <div className="col-4 offset-2 d-flex">
                        <QESCheckbox
                          containerClassName="checkbox align-self-center"
                          label="Accuracy Analysis"
                          checked={this.props.useAccuracy}
                          disabled={this.props.disabled || this.props.isZipProject}
                          onChange={this.props.handleUseAccuracyChange}
                          testId="edit-template-options__use-accuracy"
                        />
                      </div>
                    </div>
                  </>
                )}

                {this.props.useAccuracy && this.props.accuracyInsurancesIsLoading && (
                  <div className="col-4 offset-2 d-flex">
                    <LoadingSpinner />
                  </div>
                )}

                {this.props.useAccuracy && !this.props.accuracyInsurancesIsLoading && (
                  <>
                    <div className="form-group row">
                      <div className="col-12 col-sm-9 col-lg-8 offset-2">
                        {this.renderAccuracyTemplateCollections()}
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-12 col-sm-9 col-lg-8 offset-2">
                        {this.renderProjectAccuracyTemplateCorrelationIdsList()}
                      </div>
                    </div>
                  </>
                )}

                {( !this.props.accuracyInsurancesIsLoading && ((this.props.useAccuracy && this.props.shared) || (this.props.useAccuracy && this.props.specialtyToQdpCodeMapSets && this.props.specialtyToQdpCodeMapSets.length > 0))) &&
                  <div className="form-group row">
                    <div className="col-12 col-sm-9 col-lg-8 offset-2">
                      {this.renderSpecialtyToQdpCodeMapsList()}
                    </div>
                  </div>
                }

                {
                  this.props.useAccuracy && !this.props.accuracyInsurancesIsLoading && (
                    <div className="form-group row">
                      <div className="col-12 col-sm-9 col-lg-8 offset-2">
                        {this.renderAuthorizedInsurancesList()}
                      </div>
                    </div>
                  )
                }

                {this.props.useAccuracy && !this.props.accuracyInsurancesIsLoading && !this.props.useAdequacy && (
                  <>
                    <div className="form-group row">
                      <div className="col-12 col-sm-9 col-lg-8 offset-2">
                        {this.renderSpecialtyDefinitionList()}
                      </div>
                    </div>
                  </>
                )}

                {this.props.useAccuracy && !this.props.accuracyInsurancesIsLoading && !this.props.useAdequacy && (!this.props.shared && (this.props.specialtyCodeMapSets && this.props.specialtyCodeMapSets.length > 0)) &&
                  <div className="form-group row">
                    <div className="col-12 col-sm-9 col-lg-8 offset-2">
                      {this.renderMarketSpecialtyCodeMapsList()}
                    </div>
                  </div>
                }

                {!this.props.shared && !this.props.isZipProject && (
                  <>
                    <div className="form-group row">
                      <div className="col-4 offset-2">
                        <hr />
                      </div>
                    </div>

                    <div className="form-group row">
                      <div className="col-4 d-flex offset-2">
                        <QESCheckbox
                          containerClassName="checkbox align-self-center"
                          label="Adequacy Analysis"
                          checked={this.props.useAdequacy}
                          disabled={this.props.disabled}
                          onChange={this.props.handleUseAdequacyChange}
                          testId="edit-template-options__use-adequacy"
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </RoleRestricted>

          <EditAdequacyTemplateOptions
            shared={this.props.shared}
            disabled={this.props.disabled}
            useAdequacy={this.props.useAdequacy}
            project={this.props.project}
            previousTimeDistanceCalculatorType={TimeDistanceCalculatorType.Unknown}
            templateAccessGranularity={this.props.templateAccessGranularity}
            timeDistanceCalculatorTypes={this.props.timeDistanceCalculatorTypes}
            projectAccessCodeMapSets={this.props.projectAccessCodeMapSets}
            expandSpecialtyMileageCodeMapSets={this.props.expandSpecialtyMileageCodeMapSets}
            specialtyCodeMapSets={this.props.specialtyCodeMapSets}
            sortedSpecialtyGroups={this.props.sortedSpecialtyGroups}
            disagreedExcludedSpecialtyGroupCodes={null}
            templateCollections={this.props.templateCollections}
            onTemplateCollectionChange={this.props.onTemplateCollectionChange}
            onTemplateCorrelationIdChange={this.props.onTemplateCorrelationIdChange}
            onTimeDistanceCalculatorTypeChange={this.props.onTimeDistanceCalculatorTypeChange}
            onAccessCodeMapSetChange={this.props.onAccessCodeMapSetChange}
            onExpandSpecialtyMileageChange={this.props.onExpandSpecialtyMileageChange}
            onMarketSpecialtyCodeMapChange={this.props.onMarketSpecialtyCodeMapChange}
            onNetworkSpecialtyCodeMapChange={this.props.onNetworkSpecialtyCodeMapChange}
            onSpecialtyGroupsChange={this.props.onSpecialtyGroupsChange}
            onTelehealthSpecialtyGroupsChange={this.props.onTelehealthSpecialtyGroupsChange}
            user={this.props.user}
            adequacyTemplateCollectionIdRef={this.props.adequacyTemplateCollectionIdRef}
            isZipProject={this.props.isZipProject}
            perspective={this.props.perspective}
            showNetworkSpecialtyCodeMap={this.props.showNetworkSpecialtyCodeMap}
          />
        </div>
      </React.Fragment>
    );
  }

  public componentDidMount() {
    this.setState({ previousTimeDistanceCalculatorType: this.props.project.timeDistanceCalculatorType });
  }

  private renderAccuracyTemplateCollections = () => {
    if (this.props.shared) {
      return (
        <QESTextInput
          label="Accuracy Template Collection"
          className="form-control"
          disabled={true}
          value={this.props.project.accuracyTemplateCollectionId}
          testId="edit-template-options__accuracy-template-collection-text-box"
        />
      );
    } else {
      return (
        <QESSelect
          label="Accuracy Template Collection"
          className="form-control"
          disabled={this.props.disabled}
          value={this.props.project.accuracyTemplateCollectionId}
          onChange={(e) => this.props.onAccuracyTemplateCollectionChange(e.target.value)}
          testId="edit-template-options__accuracy-template-collection-select"
        >
          <option key="<none>">Select an Accuracy Template Collection</option>
          {this.props.accuracyTemplateCollections.map((collection) => (
            <option key={collection.templateCollectionId} value={collection.templateCollectionId}>
              {collection.resource && collection.resource.name || collection.templateCollectionId}
            </option>
          ))}
        </QESSelect>
      );
    }
  }

  private renderSpecialtyToQdpCodeMapsList = () => {
    if (this.props.shared) {
      return (
        <QESTextInput
          label="Specialty to BDX UID Code Map"
          className="form-control"
          disabled={true}
          value={this.props.specialtyToQdpCodeMapSets.filter((x) => x.codeMapSetId === this.props.project.accuracySpecialtyToQdpCodeMapSetId)[0].resource.name}
          testId="edit-template-options__accuracy-specialty-to-qdp-code-map-text-box"
        />
      );
    } else {
      return (
        <QESSelect
          label="Specialty to BDX UID Code Map"
          className="form-control"
          onChange={(e) => this.props.onAccuracySpecialtyToQdpCodeMapChange(+(e.target.value))}
          value={this.props.project.accuracySpecialtyToQdpCodeMapSetId
            ? this.props.project.accuracySpecialtyToQdpCodeMapSetId
            : ""}
          disabled={this.props.disabled}
          testId="edit-template-options__accuracy-specialty-to-qdp-code-map-select"
        >
          <option value="0">Select a Specialty to BDX UID Code Map</option>
          {this.props.specialtyToQdpCodeMapSets.map((set) => (
            set.codeMapSetVersions.some((v) => v.codeMapSetVersionStatus === "activated") ?
              <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
              <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
          ))}
        </QESSelect>
      );
    }
  }

  private renderSpecialtyDefinitionList = () => {
    if (this.props.shared) {
      return (
        <QESTextInput
          label="Accuracy Specialty Definition"
          className="form-control"
          disabled={true}
          value={this.props.accuracySpecialtyDefinitions.filter((x) => x.specialtyDefinitionId === parseInt(this.props.project.accuracySpecialtyDefinitionId, 10))[0].name}
          testId="edit-template-options__accuracy-specialty-definition-text-box"
        />
      );
    } else {
      return (
        <QESSelect
          label="Accuracy Specialty Definition"
          className="form-control"
          disabled={this.props.disabled}
          value={this.props.project.accuracySpecialtyDefinitionId || ""}
          onChange={this.props.onAccuracySpecialtyDefinitionIdChange}
          testId="edit-template-options__accuracy-specialty-definition-select"
        >
          <option key="<none>">Select a Specialty Definition</option>
          {this.props.accuracySpecialtyDefinitions.sort((a, b) =>
            a.name > b.name ? 1 : -1).map((option) => (
              <option key={option.specialtyDefinitionId} value={option.specialtyDefinitionId}>{option.name}</option>
            ))}
        </QESSelect>
      );
    }
  }

  private renderProjectAccuracyTemplateCorrelationIdsList = () => {
    if (this.props.shared) {
      return (
        <QESTextInput
          label="Accuracy Template"
          className="form-control"
          disabled={true}
          value={this.props.project.accuracyTemplateCorrelationId}
          testId="edit-template-options__accuracy-template-correlation-id-list-text-box"
        />
      );
    } else {
      const selectedProjectCollection =
        first(this.props.accuracyTemplateCollections.filter((item) => item.templateCollectionId ===
          this.props.project.accuracyTemplateCollectionId));
      return (
        <QESSelect
          label="Accuracy Template"
          className="form-control"
          value={this.props.project.accuracyTemplateCorrelationId}
          onChange={(e) => this.props.onAccuracyTemplateCorrelationIdChange(e.target.value)}
          disabled={this.props.disabled}
          testId="edit-template-options__accuracy-template-correlation-id-list-select"
        >
          <option>Select an Accuracy Template</option>
          {selectedProjectCollection?.correlationIds?.map((option) => (
            <option key={option} value={option}>{`${option}`}</option>
          ))}
        </QESSelect>
      );
    }
  }

  private renderMarketSpecialtyCodeMapsList = () => {
    return (
      <QESSelect
        label="Market Specialty Code Map"
        className="form-control"
        onChange={(e) => this.props.onMarketSpecialtyCodeMapChange(+(e.target.value))}
        value={this.props.project.marketSpecialtyCodeMapSetId
          ? this.props.project.marketSpecialtyCodeMapSetId
          : ""}
        disabled={this.props.disabled}
        testId="edit-template-options__market-specialty-code-map-select"
      >
        <option value="0">Select a Market Specialty Code Map</option>
        {this.props.specialtyCodeMapSets.map((set) => (
          set.codeMapSetVersions.some((v) => v.codeMapSetVersionStatus === "activated") ?
            <option key={set.codeMapSetId} value={set.codeMapSetId}>{set.resource.name}</option> :
            <option key={set.codeMapSetId} value={set.codeMapSetId} disabled={true}>{set.resource.name} - No Activated Versions</option>
        ))}
      </QESSelect>
    );
  }

  private renderAuthorizedInsurancesList = () => {
    if (this.props.shared) {
      return (
        <>
          <p className="label-s">Inspected Insurances</p>
          <ul className="list-group">
            {this.props.project.inspectedInsurancesList ? this.props.project.inspectedInsurancesList.map((ins) => (
              <li key={ins.uid} className="list-group-item">{ins.planName}: {ins.uid}</li>
            ))
              : <li key="<none>" className="list-group-item">{"<none>"}</li>
            }
          </ul>
        </>
      );
    } else {
      return (
        <>
          <div className="d-flex justify-content-between align-items-end">
            <QESSelect
              containerClassName="w-100"
              label="Inspected Insurances"
              testId="edit-template-options__inspected-insurances"
              className="form-control"
              onChange={this.props.handleAuthorizedInsurancesChange}
              disabled={this.props.disabled}
            >
              <option key="<none>">Select an insurance</option>
              {this.props.authorizedInsurances.filter((ins) =>
                this.props.selectedInsurances.findIndex((lookup) => lookup.uid === ins.uid) === -1).map(
                  (option) => (
                    <option key={option.uid} value={option.uid}>{option.planName}: {option.uid}</option>))}
            </QESSelect>
            <QESButton
              qesTestId="edit-template-options_plus"
              qesType="primary"
              onClick={() => this.props.handleAddInspectedInsurance()}
              disabled={this.props.disabled}>
                <QesIcon iconName={QesIcons.Plus} description="Add" className="align-text-top" />
              </QESButton>
          </div>
          <InsuranceList selectedInsurances={this.props.selectedInsurances} handleRemoveInspectedInsurance={this.props.handleRemoveInspectedInsurance} disabled={this.props.disabled} />
        </>
      );
    }
  }
}
